<template>
  <div class="server-inventory">
    <!--
      Freshness first, unconditionally. Plan 010 §2.1: matching runs against
      STORED inventory, so a panel that renders a component list without
      saying how old it is invites the reader to treat "we last looked three
      days ago" as "this is what is installed right now".
    -->
    <div class="server-inventory__freshness" :class="{ 'is-stale': data.stale }">
      <template v-if="data.collected_at">
        Inventory collected {{ formatRelativeTime(data.collected_at) }}
        <template v-if="data.stale"> — <strong>stale</strong>, this list may not reflect the box</template>
      </template>
      <template v-else>
        <strong>Never inventoried.</strong> This is not the same as “nothing installed” —
        the agent has not reported software for this server.
      </template>
    </div>

    <p v-if="loadError" class="server-inventory__error">{{ loadError }}</p>
    <p v-else-if="loading" class="server-inventory__empty">Loading…</p>

    <template v-else>
      <div class="server-inventory__summary">
        <div class="server-inventory__sum">
          <div class="server-inventory__sum-value">{{ data.summary?.sites ?? 0 }}</div>
          <div class="server-inventory__sum-label">Sites</div>
        </div>
        <div class="server-inventory__sum">
          <div class="server-inventory__sum-value">{{ data.summary?.total_items ?? 0 }}</div>
          <div class="server-inventory__sum-label">Components</div>
        </div>
        <div class="server-inventory__sum">
          <div class="server-inventory__sum-value">{{ data.summary?.host_items ?? 0 }}</div>
          <div class="server-inventory__sum-label">Host packages</div>
        </div>
        <div
          class="server-inventory__sum"
          :class="{ 'server-inventory__sum--bad': (data.summary?.unknown_versions ?? 0) > 0 }"
        >
          <div class="server-inventory__sum-value">{{ data.summary?.unknown_versions ?? 0 }}</div>
          <div class="server-inventory__sum-label">Unknown versions</div>
        </div>
      </div>

      <section v-for="site in data.sites || []" :key="site.id" class="server-inventory__panel">
        <header class="server-inventory__panel-head" @click="toggle(site.id)">
          <span class="server-inventory__site">{{ site.label }}</span>
          <span class="server-inventory__site-meta">
            {{ site.kind }}<template v-if="site.cms_version"> {{ site.cms_version }}</template>
            <template v-if="site.php_version"> · PHP {{ site.php_version }}</template>
            · {{ site.item_count }} components
            <template v-if="site.internet_facing"> · {{ site.internet_facing }}</template>
          </span>
          <span class="server-inventory__chevron">{{ isOpen(site.id) ? '−' : '+' }}</span>
        </header>
        <div v-if="isOpen(site.id)" class="server-inventory__table-wrap">
          <table class="server-inventory__table">
            <thead>
              <tr><th>Component</th><th>Version</th><th>Type</th><th>Managed by</th></tr>
            </thead>
            <tbody>
              <tr v-for="item in site.items" :key="item.id">
                <td>
                  <span class="server-inventory__identifier">{{ item.identifier }}</span>
                  <span class="server-inventory__meta">{{ item.ecosystem }}<template v-if="item.name"> · {{ item.name }}</template></span>
                </td>
                <td>
                  <span v-if="item.version_known" class="server-inventory__version">{{ item.version }}</span>
                  <span v-else class="server-inventory__pill">Unknown</span>
                </td>
                <td>{{ item.type || '—' }}</td>
                <td>{{ item.managed_by || '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="server-inventory__panel">
        <header class="server-inventory__panel-head" @click="toggle('host')">
          <span class="server-inventory__site">Host packages &amp; runtimes</span>
          <span class="server-inventory__site-meta">{{ (data.host_items || []).length }} items</span>
          <span class="server-inventory__chevron">{{ isOpen('host') ? '−' : '+' }}</span>
        </header>
        <div v-if="isOpen('host')" class="server-inventory__table-wrap">
          <table class="server-inventory__table">
            <thead>
              <tr><th>Package</th><th>Version</th><th>Scope</th><th>Arch</th></tr>
            </thead>
            <tbody>
              <tr v-for="item in data.host_items || []" :key="item.id">
                <td>
                  <span class="server-inventory__identifier">{{ item.identifier }}</span>
                  <span class="server-inventory__meta">{{ item.ecosystem }}</span>
                </td>
                <!--
                  Verbatim, epoch and Debian revision included: `1.1.1n-0+deb11u4`
                  is a PATCHED openssl, and a display that trimmed the revision
                  would make that impossible to see.
                -->
                <td><span class="server-inventory__version">{{ item.version || '—' }}</span></td>
                <td>{{ item.scope }}</td>
                <td>{{ item.arch || '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </template>
  </div>
</template>

<script setup>
/**
 * Per-server software inventory — Plan 010 Phase 1.
 *
 * Grouped by SITE rather than presented as one flat list, which is the §4.1
 * decision made visible: "this box has a vulnerable plugin" is useless on a
 * twenty-site host, and the grouping is what turns the same data into
 * "hrindependents.co.uk runs gravityformssmtp 1.4.2".
 */
import { onMounted, ref } from 'vue';
import useApi from '@apexl/icarus-core/src/services/api';
import commonProps from '@apexl/icarus-core/src/components/_properties/common';
import { formatRelativeTime } from '@root/services/serverFormat';

const props = defineProps({
  ...commonProps,
  apiBase: { type: String, default: '/api/v1/servers' },
  serverId: { type: [Number, String], required: true },
});

const api = useApi();
const data = ref({});
const loading = ref(true);
const loadError = ref(null);
const openSections = ref({});

function toggle(key) {
  openSections.value = { ...openSections.value, [key]: !openSections.value[key] };
}

function isOpen(key) {
  return Boolean(openSections.value[key]);
}

onMounted(async () => {
  try {
    data.value = await api.get(`${props.apiBase}/${props.serverId}/inventory`);
    // Open the first site by default — one click saved on the common case,
    // while a 1500-row host package list stays collapsed.
    const first = (data.value.sites || [])[0];
    if (first) openSections.value = { [first.id]: true };
  } catch {
    loadError.value = 'Could not load this server’s software inventory.';
  } finally {
    loading.value = false;
  }
});
</script>

<style lang="scss">
.server-inventory {
  &__freshness {
    font-family: var(--theme-font-mono);
    font-size: 11px;
    color: var(--theme-text-secondary);
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    border-left: 3px solid var(--theme-border-strong);
    border-radius: var(--theme-radius, 6px);
    padding: 8px 12px;
    margin-bottom: 14px;

    &.is-stale { border-left-color: var(--theme-status-warning); color: #8a5a0d; }
  }

  &__error {
    background: #f8dede;
    color: #a53232;
    border-radius: var(--theme-radius, 6px);
    padding: 10px 14px;
    font-size: 12.5px;
  }

  &__empty { font-size: 12.5px; color: var(--theme-text-tertiary); }

  &__summary {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    margin-bottom: 14px;

    @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  }

  &__sum {
    position: relative;
    overflow: hidden;
    padding: 12px 14px;
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius-lg, 10px);

    &::before {
      content: "";
      position: absolute;
      left: 0; top: 0; bottom: 0;
      width: 3px;
      background: var(--theme-border-strong);
    }

    &--bad::before { background: var(--theme-status-warning); }
  }

  &__sum-value { font-size: 20px; font-weight: 600; font-variant-numeric: tabular-nums; }
  &__sum-label { font-size: 11.5px; color: var(--theme-text-secondary); margin-top: 2px; }

  &__panel {
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius-lg, 10px);
    overflow: hidden;
    margin-bottom: 12px;
  }

  &__panel-head {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    border-bottom: 1px solid var(--theme-border);
    font-size: 12.5px;
    cursor: pointer;

    &:hover { background: var(--theme-bg-content); }
  }

  &__site { font-weight: 620; }

  &__site-meta {
    flex: 1;
    font-family: var(--theme-font-mono);
    font-size: 10.5px;
    color: var(--theme-text-tertiary);
  }

  &__chevron { font-family: var(--theme-font-mono); color: var(--theme-text-tertiary); }

  &__table-wrap { overflow-x: auto; }

  &__table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12.5px;

    th {
      text-align: left;
      font-size: 10px;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--theme-text-tertiary);
      font-weight: 600;
      padding: 8px 14px;
      border-bottom: 1px solid var(--theme-border);
      white-space: nowrap;
    }

    td {
      padding: 8px 14px;
      border-bottom: 1px solid var(--theme-border-soft);
      vertical-align: top;
    }

    tr:last-child td { border-bottom: 0; }
  }

  &__identifier { display: block; font-weight: 600; }

  &__meta {
    display: block;
    font-family: var(--theme-font-mono);
    font-size: 10.5px;
    color: var(--theme-text-tertiary);
    margin-top: 2px;
  }

  &__version { font-family: var(--theme-font-mono); font-size: 11.5px; }

  &__pill {
    display: inline-block;
    font-size: 9.5px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    padding: 2.5px 8px;
    border-radius: 20px;
    font-weight: 700;
    background: #fdf1de;
    color: #8a5a0d;
  }
}
</style>
